import { useEffect, useState } from "react";
import { api, formatCOP } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { AlertTriangle, Check, X, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import UsageTip from "@/components/UsageTip";
import { useHasPerm, useAuth } from "@/context/AuthContext";

const ESTADO_META = {
  pendiente: { label: "Pendiente", color: "bg-amber-100 text-amber-800" },
  aprobada: { label: "Aprobada", color: "bg-emerald-100 text-primary" },
  rechazada: { label: "Rechazada", color: "bg-rose-100 text-rose-800" },
  obsoleta: { label: "Ya no aplica", color: "bg-slate-100 text-slate-600" },
};

export default function ExcepcionesPage() {
  const { user } = useAuth();
  const isDirector = user?.role === "admin";
  const canDecide = useHasPerm("excepciones.aprobar");
  const canCreate = useHasPerm("ventas.crear");
  const puedeDecidir = isDirector || canDecide;
  // Un usuario sin capacidad de aprobar Y sin capacidad de crear pedidos
  // nunca tendrá algo que hacer aquí: mostramos una pantalla amable en vez de
  // una lista vacía.
  const sinAcceso = !puedeDecidir && !canCreate;
  const [items, setItems] = useState([]);
  const [estado, setEstado] = useState("pendiente");
  const [decidiendo, setDecidiendo] = useState(null);
  const [decision, setDecision] = useState({ tipo: "", motivo: "" });

  const load = async () => {
    if (sinAcceso) return;
    const params = new URLSearchParams();
    if (estado) params.set("estado", estado);
    const { data } = await api.get(`/excepciones?${params.toString()}`);
    setItems(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [estado, sinAcceso]);

  const [decidirSaving, setDecidirSaving] = useState(false);
  const decidir = async () => {
    setDecidirSaving(true);
    try {
      await api.post(`/excepciones/${decidiendo.id}/decidir`, {
        decision: decision.tipo, motivo: decision.motivo,
      });
      toast.success(`Excepción ${decision.tipo === "aprobar" ? "aprobada" : "rechazada"}`);
      setDecidiendo(null); setDecision({ tipo: "", motivo: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo decidir"); }
    finally { setDecidirSaving(false); }
  };

  return (
    <div>
      {sinAcceso ? (
        <div className="max-w-lg mx-auto py-20 text-center">
          <div className="w-16 h-16 mx-auto rounded-full bg-muted flex items-center justify-center mb-4">
            <ShieldCheck className="w-8 h-8 text-muted-foreground" />
          </div>
          <h1 className="font-display text-3xl font-semibold text-foreground">Sin excepciones a la vista</h1>
          <p className="text-muted-foreground mt-3">
            Esta sección solo aplica cuando armas pedidos por debajo del precio mínimo o cuando tienes permiso para aprobarlas.
            No tienes ninguno de esos permisos, así que por ahora aquí no hay nada para ti.
          </p>
        </div>
      ) : (
      <>
      <header className="flex items-end justify-between mb-8 gap-4 flex-wrap">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-muted-foreground">Control de márgenes</div>
          <h1 className="font-display text-4xl font-semibold tracking-tight text-foreground mt-2">Excepciones de precio</h1>
          <p className="text-muted-foreground mt-2">
            {puedeDecidir
              ? "Aprueba o rechaza las ventas solicitadas por debajo del precio mínimo autorizado."
              : "Consulta el estado de tus solicitudes para vender por debajo del mínimo."}
          </p>
        </div>
      </header>

      <UsageTip storageKey="excepciones" steps={puedeDecidir ? [
        "Aquí llegan las solicitudes de los vendedores para vender por debajo del mínimo.",
        "Aprueba o rechaza con motivo. Tu decisión queda registrada con fecha, hora y notifica al vendedor.",
      ] : [
        "Cuando cotizas por debajo del precio mínimo, la venta queda en excepción esperando aprobación.",
        "En este panel puedes ver el estado de tus solicitudes y el motivo si fueron rechazadas.",
      ]} />

      <div className="flex gap-1 bg-card border border-border rounded-lg p-1 w-fit mb-4">
        {["pendiente", "aprobada", "rechazada", ""].map((k) => (
          <button key={k || "todas"} onClick={() => setEstado(k)} className={`px-4 h-9 rounded-md text-sm ${estado === k ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>
            {k ? ESTADO_META[k].label : "Todas"}
          </button>
        ))}
      </div>

      <div className="space-y-3">
        {items.length === 0 && (
          <div className="py-16 text-center text-muted-foreground/70 border border-dashed border-border rounded-xl">
            <ShieldCheck className="w-8 h-8 mx-auto text-muted-foreground/50" />
            <div className="mt-3 text-sm">No hay solicitudes {estado || ""} en este momento.</div>
          </div>
        )}
        {items.map((e) => {
          const m = ESTADO_META[e.estado];
          const descuento = e.precio_minimo > 0 ? ((1 - e.precio_solicitado / e.precio_minimo) * 100).toFixed(1) : "0.0";
          return (
            <div key={e.id} className="bg-card border border-border rounded-xl p-5" data-testid={`exc-${e.id}`}>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex-1 min-w-[280px]">
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`text-xs px-2 py-1 rounded-full ${m.color}`}>{m.label}</span>
                    <span className="text-sm font-mono text-muted-foreground">{e.pedido_numero}</span>
                  </div>
                  <div className="text-base font-semibold text-foreground">{e.producto_nombre}</div>
                  <div className="text-sm text-muted-foreground mt-1">Cliente: <b className="text-foreground/80">{e.cliente_nombre}</b> · Vendedor: <b className="text-foreground/80">{e.vendedor_nombre}</b></div>
                  <div className="mt-3 grid grid-cols-3 gap-3 text-sm">
                    <div className="bg-rose-50 border border-rose-200 rounded-lg p-3">
                      <div className="text-[10px] font-bold uppercase text-rose-700">Solicita vender a</div>
                      <div className="text-lg font-semibold text-rose-900 tabular-nums">{formatCOP(e.precio_solicitado)}</div>
                    </div>
                    <div className="bg-muted/40 border border-border rounded-lg p-3">
                      <div className="text-[10px] font-bold uppercase text-muted-foreground">Mínimo autorizado</div>
                      <div className="text-lg font-semibold text-foreground tabular-nums">{formatCOP(e.precio_minimo)}</div>
                    </div>
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                      <div className="text-[10px] font-bold uppercase text-amber-700">Descuento</div>
                      <div className="text-lg font-semibold text-amber-900">{descuento}% <span className="text-xs">bajo mínimo</span></div>
                    </div>
                  </div>
                  <div className="mt-3 p-3 bg-muted/40 border border-border rounded-lg text-sm">
                    <b>Motivo del vendedor:</b> {e.motivo}
                  </div>
                  {e.decision_at && (
                    <div className="mt-2 text-xs text-muted-foreground">
                      {e.estado === "obsoleta" ? "Ya no aplica (cerrada automáticamente)" : <>{e.estado === "aprobada" ? <><Check className="w-3 h-3 inline -mt-0.5 mr-0.5" />Aprobada</> : <><X className="w-3 h-3 inline -mt-0.5 mr-0.5" />Rechazada</>} por {e.aprobador_nombre}</>} el {new Date(e.decision_at).toLocaleString("es-CO")}
                      {e.decision_motivo && <> · Motivo: {e.decision_motivo}</>}
                    </div>
                  )}
                </div>
                {e.estado === "pendiente" && puedeDecidir && (
                  <div className="flex gap-2">
                    <button onClick={() => { setDecidiendo(e); setDecision({ tipo: "aprobar", motivo: "" }); }} className="h-10 px-4 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 text-sm font-medium flex items-center gap-2" data-testid={`aprobar-${e.id}`}>
                      <Check className="w-4 h-4" /> Aprobar
                    </button>
                    <button onClick={() => { setDecidiendo(e); setDecision({ tipo: "rechazar", motivo: "" }); }} className="h-10 px-4 rounded-lg bg-rose-600 text-white hover:bg-rose-700 text-sm font-medium flex items-center gap-2" data-testid={`rechazar-${e.id}`}>
                      <X className="w-4 h-4" /> Rechazar
                    </button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={!!decidiendo} onOpenChange={(v) => { if (decidirSaving) return; if (!v) setDecidiendo(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">{decision.tipo === "aprobar" ? "Aprobar" : "Rechazar"} solicitud</DialogTitle>
            <p className="text-sm text-muted-foreground">{decidiendo?.producto_nombre} · {formatCOP(decidiendo?.precio_solicitado || 0)}</p>
          </DialogHeader>
          <div className="mt-4 space-y-3">
            <Input value={decision.motivo} onChange={(e) => setDecision({ ...decision, motivo: e.target.value })} placeholder="Motivo de tu decisión (opcional)" className="h-11" data-testid="decision-motivo" />
            <Button onClick={decidir} disabled={decidirSaving} className={`w-full h-11 ${decision.tipo === "aprobar" ? "bg-primary hover:bg-primary/90" : "bg-rose-600 hover:bg-rose-700"} text-white`} data-testid="decision-confirmar">
              {decidirSaving ? "Enviando..." : `Confirmar ${decision.tipo === "aprobar" ? "aprobación" : "rechazo"}`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      </>
      )}
    </div>
  );
}
